import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import useInView from './useInView';
import { MOTION, easeOut } from './landingTokens';

// ─── CountUp ──────────────────────────────────────────────────────────────────
// Tabular numeral that counts up to `value` when scrolled into view, resetting
// when scrolled out (matches the section's repeat semantics). Scoped to the
// verdict card's scores — where the number IS the content — per the "subtle"
// animation review. Writes via rAF directly to the node (no re-renders).
export default function CountUp({ value, decimals = 0, component = 'span', sx }) {
  const [ref, inView, instant] = useInView();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const paint = (f) => { el.textContent = (f * Number(value)).toFixed(decimals); };
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { paint(1); return undefined; }
    if (!inView) { paint(0); return undefined; }
    // Entering from above (scroll-up): final state, no count-up.
    if (instant) { paint(1); return undefined; }
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / MOTION.duration, 1);
      paint(easeOut(p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, instant, value, decimals, ref]);

  return (
    <Box component={component} ref={ref} sx={{ fontFeatureSettings: "'tnum' 1", ...sx }}>
      {Number(value).toFixed(decimals)}
    </Box>
  );
}
